import {
  CClassEavTargetValuesEnum,
  CClassUoTargetValuesEnum,
  PClassEavTargetValuesEnum,
  PClassEminTargetValuesEnum,
  CCustomClassData,
  PCustomClassData,
} from "types";
import { CapturedHeatmaps, CarboneLightingClassConfig, MetStatus } from "./types";
import { roundToTwo, checkMetStatus, getStatusText, statusToColor } from "./status";

export function getBorderTargetEav(side: "left" | "right", lightingClassConfig?: CarboneLightingClassConfig): string {
  const borderClass = side === "left" ? lightingClassConfig?.borderAreaLeftClasses : lightingClassConfig?.borderAreaRightClasses;
  const customClass = side === "left" ? lightingClassConfig?.customClasses?.leftBorderCustomClass : lightingClassConfig?.customClasses?.rightBorderCustomClass;

  if (customClass?.class_type) return String((customClass as CCustomClassData | PCustomClassData).Em_av || "");
  if (borderClass?.includes("C")) return CClassEavTargetValuesEnum[borderClass as keyof typeof CClassEavTargetValuesEnum] || "";
  if (borderClass?.includes("P")) return PClassEavTargetValuesEnum[borderClass as keyof typeof PClassEavTargetValuesEnum] || "";
  return "";
}

export function getBorderTargetEmin(side: "left" | "right", lightingClassConfig?: CarboneLightingClassConfig): string {
  const borderClass = side === "left" ? lightingClassConfig?.borderAreaLeftClasses : lightingClassConfig?.borderAreaRightClasses;
  const customClass = side === "left" ? lightingClassConfig?.customClasses?.leftBorderCustomClass : lightingClassConfig?.customClasses?.rightBorderCustomClass;

  if (customClass?.class_type?.includes("P")) return String((customClass as PCustomClassData).Em_min || "");
  if (borderClass?.includes("P")) return PClassEminTargetValuesEnum[borderClass as keyof typeof PClassEminTargetValuesEnum] || "";
  return "";
}

export function getBorderTargetUo(side: "left" | "right", lightingClassConfig?: CarboneLightingClassConfig): string {
  const borderClass = side === "left" ? lightingClassConfig?.borderAreaLeftClasses : lightingClassConfig?.borderAreaRightClasses;
  const customClass = side === "left" ? lightingClassConfig?.customClasses?.leftBorderCustomClass : lightingClassConfig?.customClasses?.rightBorderCustomClass;

  if (customClass?.class_type?.includes("C")) return String((customClass as CCustomClassData).E_uo || "");
  if (borderClass?.includes("C")) return CClassUoTargetValuesEnum[borderClass as keyof typeof CClassUoTargetValuesEnum] || "";
  return "";
}

type SubValItem = { name: string; e_avg: number; e_min: number; u_oe: number; u_de?: number };

export function getSubValData(subValResult: unknown, sideName: string): SubValItem | null {
  const arr = subValResult as SubValItem[] | null;
  if (!arr || !Array.isArray(arr)) return null;
  return arr.find((item) => item.name === sideName) || null;
}

type BuildIlluminanceTableParams = {
  illuminanceData?: Record<string, any>;
  lightingClassConfig?: CarboneLightingClassConfig;
  roadClass: string;
  heatmaps?: CapturedHeatmaps;
  f_ti?: string;
  ulor?: string;
  t: (key: string) => string;
};

export function buildIlluminanceTable({ illuminanceData, lightingClassConfig, roadClass, heatmaps, f_ti, ulor, t }: BuildIlluminanceTableParams) {
  if (!illuminanceData) {
    return { summary: {}, target_values: {}, heatmap_base64: heatmaps?.illuminance || undefined };
  }

  const customClass = lightingClassConfig?.customClasses?.mainCustomClass as CCustomClassData | PCustomClassData | undefined;
  let targetEav = "";
  let targetEmin = "";
  let targetU0 = "";

  if (customClass?.class_type) {
    if (customClass.class_type.includes("C")) {
      targetEav = String((customClass as CCustomClassData).Em_av || "");
      targetU0 = String((customClass as CCustomClassData).E_uo || "");
    } else if (customClass.class_type.includes("P")) {
      targetEav = String((customClass as PCustomClassData).Em_av || "");
      targetEmin = String((customClass as PCustomClassData).Em_min || "");
    }
  } else if (roadClass.includes("C")) {
    targetEav = CClassEavTargetValuesEnum[roadClass as keyof typeof CClassEavTargetValuesEnum] || "";
    targetU0 = CClassUoTargetValuesEnum[roadClass as keyof typeof CClassUoTargetValuesEnum] || "";
  } else if (roadClass.includes("P")) {
    targetEav = PClassEavTargetValuesEnum[roadClass as keyof typeof PClassEavTargetValuesEnum] || "";
    targetEmin = PClassEminTargetValuesEnum[roadClass as keyof typeof PClassEminTargetValuesEnum] || "";
  }

  const resultEav = roundToTwo(illuminanceData.e_avg || 0);
  const resultEmin = roundToTwo(illuminanceData.e_min || 0);
  const resultU0 = roundToTwo(illuminanceData.u_oe || 0);
  const resultUd = roundToTwo(illuminanceData.u_de || 0);

  const eAvStatus = checkMetStatus(resultEav, Number.parseFloat(targetEav) || 0, "gte");
  const eMinStatus = targetEmin ? checkMetStatus(resultEmin, Number.parseFloat(targetEmin) || 0, "gte") : ("met" as MetStatus);
  const u0Status = targetU0 ? checkMetStatus(resultU0, Number.parseFloat(targetU0) || 0, "gte") : ("met" as MetStatus);

  const ftiValueForTable = f_ti ? roundToTwo(Number(f_ti)) : null;
  const rulValueForTable = ulor ? `${Math.round(Number(ulor))} %` : "";

  return {
    summary: {
      target_class: customClass?.class_type || roadClass,
      e_av: String(resultEav),
      e_av_status: eAvStatus,
      e_av_status_text: getStatusText(eAvStatus, t),
      e_av_status_color: statusToColor(eAvStatus),
      e_min: String(resultEmin),
      e_min_status: eMinStatus,
      e_min_status_text: getStatusText(eMinStatus, t),
      e_min_status_color: statusToColor(eMinStatus),
      u_0: String(resultU0),
      u_0_status: u0Status,
      u_0_status_text: getStatusText(u0Status, t),
      u_0_status_color: statusToColor(u0Status),
      u_d: String(resultUd),
      f_ti: ftiValueForTable !== null ? `${ftiValueForTable} %` : "",
      r_ul: rulValueForTable,
    },
    target_values: { e_av_min: targetEav, e_min: targetEmin, u_0_min: targetU0 },
    heatmap_base64: heatmaps?.illuminance || undefined,
  };
}

type BuildBorderDataParams = {
  side: "left" | "right";
  sidewalkData: SubValItem | null;
  lightingClassConfig?: CarboneLightingClassConfig;
  heatmaps?: CapturedHeatmaps;
  ftiValue: number | null;
  rulValue: string;
  t: (key: string) => string;
};

export function buildBorderData({ side, sidewalkData, lightingClassConfig, heatmaps, ftiValue, rulValue, t }: BuildBorderDataParams) {
  if (!sidewalkData) return null;

  const targetEav = getBorderTargetEav(side, lightingClassConfig);
  const targetEmin = getBorderTargetEmin(side, lightingClassConfig);
  const targetUo = getBorderTargetUo(side, lightingClassConfig);
  const borderClass = side === "left" ? lightingClassConfig?.borderAreaLeftClasses : lightingClassConfig?.borderAreaRightClasses;
  const heatmapBase64 =
    side === "left" ? heatmaps?.borderLeft || heatmaps?.illuminanceLeft || undefined : heatmaps?.borderRight || heatmaps?.illuminanceRight || undefined;

  const eAvStatus = checkMetStatus(sidewalkData.e_avg || 0, Number.parseFloat(targetEav) || 0, "gte");
  const eMinStatus = checkMetStatus(sidewalkData.e_min || 0, Number.parseFloat(targetEmin) || 0, "gte");

  return {
    target_class: borderClass || "",
    target_e_av: targetEav,
    e_av: String(roundToTwo(sidewalkData.e_avg || 0)),
    e_av_status: eAvStatus,
    e_av_status_text: getStatusText(eAvStatus, t),
    e_av_status_color: statusToColor(eAvStatus),
    target_e_min: targetEmin,
    e_min: String(roundToTwo(sidewalkData.e_min || 0)),
    e_min_status: eMinStatus,
    e_min_status_text: getStatusText(eMinStatus, t),
    e_min_status_color: statusToColor(eMinStatus),
    u_o: sidewalkData.u_oe ? String(roundToTwo(sidewalkData.u_oe)) : "",
    u_d: sidewalkData.u_de ? String(roundToTwo(sidewalkData.u_de)) : "",
    f_ti: ftiValue !== null ? `${ftiValue} %` : "",
    r_ul: rulValue,
    target_u_o: targetUo,
    heatmap_base64: heatmapBase64,
  };
}
